import type { Filters, LocationSummary } from '../lib/consts';
import useStore from '../lib/store';
import { supabase } from '../supabaseClient';
import { useEffect, useState } from 'react';

type GeoLocationRow = {
  location_name: string;
  latitude: number;
  longitude: number;
  rental_count?: number | null;
};

type LocationCountRow = {
  location_name: string;
  rental_count: number;
};

type LocationCountFilters = Pick<Filters, 'min_bedrooms'> & {
  minPostedAt: Date;
};

const fetchLocations = async (filters: LocationCountFilters): Promise<Map<string, LocationSummary>> => {
  const { data, error } = await supabase
    .from('geo_location')
    .select('location_name,latitude,longitude,rental_count');

  if (error) {
    console.error('Error fetching locations:', error);
    throw error;
  }

  const minBedrooms = filters.min_bedrooms ? Number(filters.min_bedrooms) : null;
  const { data: liveCounts, error: countError } = await supabase.rpc('get_visible_location_counts', {
    p_min_posted_at: filters.minPostedAt.toISOString(),
    p_min_bedrooms: Number.isFinite(minBedrooms) ? minBedrooms : null,
  });

  if (countError) {
    console.warn('Falling back to cached location counts:', countError);
  }

  const counts = new Map(
    ((liveCounts || []) as LocationCountRow[])
      .map(location => [location.location_name.toLowerCase(), location.rental_count])
  );

  return new Map(((data || []) as GeoLocationRow[])
    .map(location => {
      const key = location.location_name.toLowerCase();
      const rentalCount = countError ? location.rental_count || 0 : counts.get(key) || 0;
      return [key, {
        name: location.location_name,
        lat: location.latitude,
        lng: location.longitude,
        rentalCount,
      } as LocationSummary] as const;
    })
    .filter(([, location]) => location.rentalCount > 0));
}

export function useLocations(filters: Filters, minPostedAt: Date) {
  const locations = useStore((state) => state.map.locations);
  const setMap = useStore((state) => state.setMap);
  const [loading, setLoading] = useState(true);
  const { min_bedrooms } = filters;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchLocations({ min_bedrooms, minPostedAt })
      .then((res) => {
        if (cancelled) return;
        setMap({ locations: res });
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to fetch locations:', err);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [minPostedAt, min_bedrooms, setMap]);

  return { locations, loading };
}
